import keyBy from 'lodash/keyBy';
import { isToggleEnabled } from 'selectors/flowDesigner/utils/getUpdatedAvailablePaletteItemsByToggle';

// look up based on enum returned from db

function getIncidentResolutionPackageUrl(featureToggleMap) {
  const baseUrl =
    'https://static.xmatters.com/integrations/xmatters/IncidentResolution-1';

  return isToggleEnabled(
    [
      'SERVICES_LIST_AND_DETAILS',
      'IMPACTED_SERVICES_IN_INITIATE_INCIDENT_TEMPLATE',
    ],
    featureToggleMap,
  )
    ? `${baseUrl}-11.zip`
    : `${baseUrl}-8.zip`;
}

/*
This constant is used to load both integration cards, and the individual config pages.
Each contains the following fields: {
  id: The integration config type.
  title: The title displayed on the card and at the top of the config page.
  categories: The categories shown on the integration card
  targetFormNames: Array of the names of forms used in the target integration config template
  hideRecipients: true if the recipient selector is hidden for this config page
}
  The typeOverride prop is used to point an integration card to another integration's config page
*/

export const CUSTOM_WORKFLOW = {
  id: 'CUSTOM_WORKFLOW',
  titleId: 'CUSTOM_WORKFLOW',
  categories: ['ANYTHING_AND_EVERYTHING'],
  sortOrder: 0,
  cshid: 'CreateWorkflow',
  // COR-48663: Remove this
  // entry from the unfiltered
  // workflow templates
  hideUnlessSearched: true,
};

export const PACKAGED = {
  JIRA: {
    id: 'JIRA',
    title: 'JIRA Cloud',
    categories: ['IT_SERVICE_MANAGEMENT', 'PLANNING_TOOLS'],
    targetFormNames: [
      'Automated notification',
      'Conference Bridge',
      'Engage with xMatters',
    ],
    descriptionIDs: ['JIRA_MARKETING_DESC_1', 'JIRA_MARKETING_DESC_2'],
  },
  JIRA_SERVER: {
    id: 'JIRA_SERVER',
    title: 'JIRA Server',
    categories: ['IT_SERVICE_MANAGEMENT', 'PLANNING_TOOLS'],
    targetFormNames: [
      'Engage with xMatters',
      'Engage with xMatters - Conference',
      'Notify Assignee',
    ],
    imageOverride: 'JIRA',
    descriptionIDs: [
      'JIRA_SERVER_MARKETING_DESC_1',
      'JIRA_SERVER_MARKETING_DESC_2',
    ],
  },
  MS_TEAMS: {
    id: 'MS_TEAMS',
    title: 'Microsoft Teams',
    categories: ['CHAT'],
    targetFormNames: ['Invite to Channel'],
    descriptionIDs: ['MS_TEAMS_MARKETING_DESC_1', 'MS_TEAMS_MARKETING_DESC_2'],
    additionalInfoIDs: ['MS_TEAMS_WORKFLOW_INSTALLATION_INFO'],
    customHelpMessageId: 'MS_TEAMS_WORKFLOW_HELP',
    customHelpLinkId: 'WORKFLOW_GUIDE',
    cancelButtonCustomLabelId: 'CLOSE',
    hideCallToActionBtn: true,
  },
  SENSU: {
    id: 'SENSU',
    title: 'Sensu',
    categories: ['MONITORING'],
    targetFormNames: ['Sensu Alert'],
    descriptionIDs: ['SENSU_MARKETING_DESC_1', 'SENSU_MARKETING_DESC_2'],
    link: 'https://www.xmatters.com/integration/sensu/',
  },
  SLACK_BOT: {
    id: 'SLACK_BOT',
    title: 'Slack',
    categories: ['CHAT'],
    targetFormNames: ['Channel invitations'],
    imageOverride: 'SLACK',
    descriptionIDs: [
      'SLACK_BOT_MARKETING_DESC_1',
      'SLACK_BOT_MARKETING_DESC_2',
    ],
  },
  SPLUNK: {
    id: 'SPLUNK',
    title: 'Splunk',
    categories: ['LOG_MANAGEMENT'],
    targetFormNames: ['Alert'],
    hideRecipients: true,
    descriptionIDs: ['SPLUNK_MARKETING_DESC_1', 'SPLUNK_MARKETING_DESC_2'],
  },
  SPLUNK_ITSI: {
    id: 'SPLUNK_ITSI',
    title: 'Splunk IT Service Intelligence',
    shortTitle: 'Splunk ITSI',
    categories: ['LOG_MANAGEMENT'],
    targetFormNames: ['Notable Events'],
    hideRecipients: true,
    imageOverride: 'SPLUNK',
    descriptionIDs: [
      'SPLUNK_ITSI_MARKETING_DESC_1',
      'SPLUNK_ITSI_MARKETING_DESC_2',
    ],
  },
  WEBHOOK: {
    id: 'WEBHOOK',
    title: 'Webhook',
    categories: ['ANYTHING_AND_EVERYTHING'],
    targetFormNames: ['Webhook'],
    descriptionIDs: ['WEBHOOK_MARKETING_DESC_1', 'WEBHOOK_MARKETING_DESC_2'],
  },
};

// These are deprecated integrations that no one should create new configs for.
export const DEPRECATED = {
  AWS_CLOUDWATCH: {
    id: 'AWS_CLOUDWATCH',
    title: 'AWS Cloudwatch',
    categories: ['APPLICATION_PERFORMANCE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/aws-cloudwatch/',
    imageOverride: 'AWS',
  },
  CA_SERVICE_DESK: {
    id: 'CA_SERVICE_DESK',
    title: 'CA Service Desk Manager',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/ca-service-desk-manager-incident-management ',
    imageOverride: 'CA',
  },
  CA_SPECTRUM: {
    id: 'CA_SPECTRUM',
    title: 'CA Spectrum',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/ca-spectrum/',
    imageOverride: 'CA',
  },
  CHERWELL: {
    id: 'CHERWELL',
    title: 'Cherwell',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/cherwell/',
  },
  GRAFANA: {
    id: 'GRAFANA',
    title: 'Grafana',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/grafana/',
  },
  GRAFANA_LEGACY_ALERTS: {
    id: 'GRAFANA_WORKFLOW',
    title: 'Grafana',
    categories: ['MONITORING'],
    descriptionIDs: ['GRAFANA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'GRAFANA',
    cshid: 'Grafana',
    webhookCshid: 'GrafanaWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/grafana/Grafana-1-2.zip',
  },
  HIPCHAT: {
    id: 'HIPCHAT',
    title: 'HipChat',
    categories: ['CHAT'],
    targetFormNames: ['Invite to room'],
    hideRecipients: true,
    descriptionIDs: ['HIPCHAT_MARKETING_DESC_1', 'HIPCHAT_MARKETING_DESC_2'],
  },
  HONEYCOMB: {
    id: 'HONEYCOMB',
    title: 'Honeycomb',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/honeycomb-io/',
  },
  HP_SMIM: {
    id: 'HP_SMIM',
    title: 'HP Service Manager ',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/hp-service-manager/',
    imageOverride: 'HP',
  },
  IBM_NETCOOL: {
    id: 'IBM_NETCOOL',
    title: 'IBM NetCool',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/ibm-netcool-omnibus/',
    imageOverride: 'IBM',
  },
  INSTANA: {
    id: 'INSTANA',
    title: 'Instana',
    categories: ['APPLICATION_PERFORMANCE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/instana/',
  },
  LIGHTSTEP: {
    id: 'LIGHTSTEP',
    title: 'Lightstep',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/lightstep/',
  },
  LOGGLY: {
    id: 'LOGGLY',
    title: 'Loggly',
    categories: ['LOG_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/loggly/',
  },
  LOGZIO: {
    id: 'LOGZIO',
    title: 'Logz.io',
    categories: ['LOG_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/logzio/',
  },
  MICROSOFT_AZURE: {
    id: 'MICROSOFT_AZURE',
    title: 'Microsoft Azure Monitor',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/azure-monitor/',
  },
  MOOGSOFT: {
    id: 'MOOGSOFT',
    title: 'Moogsoft',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/moogsoft/',
  },
  NAGIOS_CORE: {
    id: 'NAGIOS_CORE',
    title: 'Nagios Core',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/nagios/',
  },
  NAGIOS: {
    id: 'NAGIOS',
    title: 'Nagios XI',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/nagios-xi/',
  },
  OVER_OPS: {
    id: 'OVER_OPS',
    title: 'Overops',
    categories: ['APPLICATION_PERFORMANCE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/overops/',
  },
  PANOPTA: {
    id: 'PANOPTA',
    title: 'Panopta',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/panopta/',
  },
  PG_DASH: {
    id: 'PG_DASH',
    title: 'pgDash',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/pgdash/',
  },
  PROMETHEUS: {
    id: 'PROMETHEUS',
    title: 'Prometheus',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/prometheus/',
  },
  RUNSCOPE: {
    id: 'RUNSCOPE',
    title: 'Runscope',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/runscope/',
  },
  SOLAR_WINDS_LIBRATO: {
    id: 'SOLAR_WINDS_LIBRATO',
    title: 'Librato',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/librato/',
  },
  SOLAR_WINDS_PAPERTRAIL: {
    id: 'SOLAR_WINDS_PAPERTRAIL',
    title: 'PaperTrail',
    categories: ['LOG_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/papertrail/',
  },
  SEMATEXT: {
    id: 'SEMATEXT',
    title: 'Sematext',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/sematext/',
  },
  SENTRY: {
    id: 'SENTRY',
    title: 'Sentry',
    categories: ['APPLICATION_PERFORMANCE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/sentry-io-alerts/',
  },
  SITE24X7: {
    id: 'SITE24X7',
    title: 'Site24x7',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/site24x7/',
  },
  SYSDIG: {
    id: 'SYSDIG',
    title: 'Sysdig',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/sysdig/',
  },
  THOUSAND_EYES: {
    id: 'THOUSAND_EYES',
    title: 'ThousandEyes',
    categories: ['APPLICATION_PERFORMANCE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/thousandeyes/',
  },
  ZABBIX: {
    id: 'ZABBIX',
    title: 'Zabbix',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/zabbix/',
  },
};

// INCIDENT_MANAGEMENT is not a built in integration, but we used integration_config_type
// to tag the workflow at Criag's recommendation
const NOT_INTEGRATIONS = {
  INCIDENT_MANAGEMENT: {
    imageOverride: 'INCIDENT_RESOLUTION',
  },
};

// Just an array of all the non packaged integrations
export const NON_PACKAGED = [
  {
    id: 'API_FORTRESS',
    title: 'API Fortress',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/api-fortress/',
  },
  {
    id: 'APPDYNAMICS_WORKFLOW',
    title: 'AppDynamics',
    categories: ['MONITORING'],
    descriptionIDs: ['APPDYNAMICS_WORKFLOW_DESCRIPTION'],
    imageOverride: 'APP_DYNAMICS',
    cshid: 'AppDynamics',
    webhookCshid: 'AppDynamicsWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/appdynamics/AppDynamics-1-4.zip',
  },
  {
    id: 'APPNETA',
    title: 'AppNeta',
    categories: ['MONITORING'],
    targetFormNames: ['AppNeta Alert'],
    link: 'https://www.xmatters.com/integration/appneta/',
  },
  {
    id: 'APPOPTICS_WORKFLOW',
    title: 'SolarWinds AppOptics',
    categories: ['MONITORING'],
    descriptionIDs: ['APPOPTICS_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SOLARWINDS',
    cshid: 'AppOptics',
    webhookCshid: 'AppOpticsWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/appoptics/AppOptics-1-2.zip',
  },
  {
    id: 'ATLASSIAN_BITBUCKET_PIPELINES',
    title: 'Bitbucket Pipelines',
    categories: ['CONTINUOUS_INTEGRATION'],
    link: 'https://www.xmatters.com/integration/bitbucket-pipelines/',
  },
  {
    id: 'AWS_CLOUDWATCH_V2_WORKFLOW',
    title: 'AWS CloudWatch',
    categories: ['MONITORING'],
    descriptionIDs: ['AWS_CLOUDWATCH_V2_WORKFLOW_DESCRIPTION'],
    imageOverride: 'AWS_CLOUDWATCH',
    cshid: 'AWSCloudWatch',
    webhookCshid: 'AWSCloudWatchWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/aws-cloudwatch/AWSCloudWatch-1-6.zip',
  },
  {
    id: 'AZURE_PIPELINES_CHANGE_WORKFLOW',
    title: 'Microsoft Azure Pipelines',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['AZURE_PIPELINES_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'AZURE_PIPELINES',
    cshid: 'AzurePipelines',
    webhookCshid: 'AzurePipelinesWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/azure-pipelines/AzurePipelines-1.zip',
  },
  {
    id: 'AZURE_WORKFLOW',
    title: 'Microsoft Azure Monitor',
    categories: ['MONITORING'],
    descriptionIDs: ['AZURE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'MICROSOFT_AZURE',
    cshid: 'AzureMonitor',
    webhookCshid: 'AzureMonitorWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/ms-azure/MicrosoftAzureMonitor-1-5.zip',
  },
  {
    id: 'BIGPANDA_WORKFLOW',
    title: 'BigPanda',
    categories: ['MONITORING'],
    descriptionIDs: ['BIGPANDA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'BIGPANDA',
    cshid: 'BigPanda',
    webhookCshid: 'BigPandaWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/bigpanda/BigPanda-1-4.zip',
  },
  {
    id: 'BITBUCKET_PIPELINES_CHANGE_WORKFLOW',
    title: 'Bitbucket Pipelines Changes',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['BITBUCKET_PIPELINES_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'BITBUCKET',
    cshid: 'BitbucketPipelines',
    webhookCshid: 'BitbucketPipelinesWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/bitbucket-pipelines/BitbucketPipelines-1.zip',
  },
  {
    id: 'BLAZEMETER_WORKFLOW',
    title: 'BlazeMeter',
    categories: ['MONITORING'],
    descriptionIDs: ['BLAZEMETER_WORKFLOW_DESCRIPTION'],
    imageOverride: 'BLAZEMETER',
    cshid: 'BlazeMeter',
    webhookCshid: 'BlazeMeterWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/blazemeter/BlazeMeter-1-3.zip',
  },
  {
    id: 'BMC_BPPM',
    title: 'BMC BPPM/TrueSight',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/bmc-bppm/',
    imageOverride: 'BMC',
  },
  {
    id: 'BMC_CONTROL_M',
    title: 'BMC Control-M',
    categories: ['WORKLOAD_AUTOMATION'],
    link: 'https://www.xmatters.com/integration/bmc-control-m/',
    imageOverride: 'BMC',
  },
  {
    id: 'BMC_REMEDY_9',
    shortTitle: 'BMC Remedy 9 Incident',
    title: 'BMC Remedy 9 Incident Management',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/bmc-remedy9/',
    imageOverride: 'BMC',
  },
  {
    id: 'BMC_REMEDY_9_CHANGE_MANAGEMENT',
    shortTitle: 'BMC Remedy 9 Change',
    title: 'BMC Remedy 9 Change Management',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/bmc-remedy-itsm-change-request-management/',
    imageOverride: 'BMC',
  },
  {
    id: 'BMC_REMEDY_FORCE',
    title: 'BMC Remedyforce',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/bmc-remedyforce/',
    imageOverride: 'BMC',
  },
  {
    id: 'BMC_TRUE_SIGHT_PULSE',
    title: 'BMC TrueSight Pulse',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/boundary/',
    imageOverride: 'BMC',
  },
  {
    id: 'CA_AGILE_CENTRAL',
    shortTitle: 'CA Agile',
    title: 'CA Agile Central Rally',
    categories: ['IT_SERVICE_MANAGEMENT', 'PLANNING_TOOLS'],
    link: 'https://www.xmatters.com/integration/ca-agile-central/',
    imageOverride: 'CA',
  },
  {
    id: 'CA_UNIFIED_INFRASTRUCTURE_MANAGEMENT',
    title: 'CA UIM',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/ca-unified-infrastructure-management/',
    imageOverride: 'CA',
  },
  {
    id: 'CHEF',
    title: 'Chef',
    categories: ['CONFIGURATION_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/chef/',
    descriptionIDs: ['CHEF_MARKETING_DESC_1', 'CHEF_MARKETING_DESC_2'],
  },
  {
    id: 'CHERWELL_WORKFLOW',
    title: 'Cherwell',
    categories: ['IT_SERVICE_MANAGEMENT'],
    descriptionIDs: ['CHERWELL_WORKFLOW_DESCRIPTION'],
    imageOverride: 'CHERWELL',
    cshid: 'Cherwell',
    webhookCshid: 'CherwellWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/cherwell/Cherwell-2.zip',
  },
  {
    id: 'CIRCLE_CI',
    title: 'CircleCI',
    categories: ['CONTINUOUS_INTEGRATION'],
    link: 'https://www.xmatters.com/integration/circleci-orb/',
  },
  {
    id: 'CISCO_PRIME',
    shortTitle: 'Cisco Prime',
    title: 'Cisco Prime',
    categories: ['CONFIGURATION_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/cisco-prime/',
  },
  {
    id: 'CISCO_SPARK',
    shortTitle: 'Cisco Webex Teams',
    title: 'Cisco Webex Teams (formerly known as Spark)',
    categories: ['CHAT'],
    link: 'https://www.xmatters.com/integration/cisco-webex-teams/',
  },
  {
    id: 'DATADOG_WORKFLOW',
    title: 'Datadog',
    categories: ['MONITORING'],
    descriptionIDs: ['DATADOG_WORKFLOW_DESCRIPTION'],
    imageOverride: 'DATADOG',
    cshid: 'Datadog',
    webhookCshid: 'DatadogWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/datadog/Datadog-1-4.zip',
  },
  {
    id: 'DYNATRACE_CLOUD_AUTOMATION_WORKFLOW',
    title: 'Dynatrace Cloud Automation',
    categories: ['MONITORING'],
    descriptionIDs: ['DYNATRACE_CLOUD_AUTOMATION_WORKFLOW_DESCRIPTION'],
    imageOverride: 'DYNATRACE',
    cshid: 'DynatraceCA',
    webhookCshid: 'DynatraceCAWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/dynatrace-cloud-automation/DynatraceCloudAutomation-2.zip',
  },
  {
    id: 'DYNATRACE_WORKFLOW',
    title: 'Dynatrace',
    categories: ['MONITORING'],
    descriptionIDs: ['DYNATRACE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'DYNATRACE',
    cshid: 'Dynatrace',
    webhookCshid: 'DynatraceWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/dynatrace/Dynatrace-1-5.zip',
  },
  {
    id: 'EASY_VISTA',
    title: 'EasyVista',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/easyvista/',
  },
  {
    id: 'ELASTIC_CONNECTOR_WORKFLOW',
    title: 'Elastic',
    categories: ['MONITORING'],
    descriptionIDs: ['ELASTIC_CONNECTOR_WORKFLOW_DESCRIPTION'],
    imageOverride: 'ELASTIC',
    cshid: 'Elastic',
    webhookCshid: 'ElasticWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/elastic/Elastic-1-5.zip',
  },

  {
    id: 'EVERBRIDGE_SIGNAL_WORKFLOW',
    title: 'Everbridge Signal',
    categories: ['SECURITY'],
    descriptionIDs: ['EVERBRIDGE_SIGNAL_WORKFLOW_DESCRIPTION'],
    imageOverride: 'EVERBRIDGE_SIGNAL',
    cshid: 'EverbridgeSignal',
    webhookCshid: 'EverbridgeSignalWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/everbridge-signal/EverbridgeSignal-2.zip',
  },
  {
    id: 'FRESHDESK_WORKFLOW',
    title: 'Freshdesk',
    categories: ['IT_SERVICE_MANAGEMENT'],
    descriptionIDs: ['FRESHDESK_WORKFLOW_DESCRIPTION'],
    imageOverride: 'FRESHDESK',
    cshid: 'Freshdesk',
    webhookCshid: 'FreshdeskWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/freshdesk/Freshdesk-1-2.zip',
  },
  {
    id: 'FRESHSERVICE',
    title: 'Freshservice',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/freshservice/',
  },
  {
    id: 'GITHUB_CHANGE_WORKFLOW',
    title: 'GitHub Changes',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['GITHUB_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'GITHUB',
    cshid: 'GitHub',
    webhookCshid: 'GitHubWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl: 'https://static.xmatters.com/integrations/github/GitHub-1.zip',
  },
  {
    id: 'GITLAB_CHANGE_WORKFLOW',
    title: 'GitLab',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['GITLAB_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'GITLAB',
    cshid: 'Gitlab',
    webhookCshid: 'GitlabWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl: 'https://static.xmatters.com/integrations/gitlab/GitLab-1.zip',
  },
  {
    id: 'GOOGLE_CLOUD_OPS_WORKFLOW',
    title: 'Google Cloud Operations Suite',
    categories: ['MONITORING'],
    targetFormNames: ['Google Cloud Monitoring'],
    descriptionIDs: ['GOOGLE_CLOUD_OPS_WORKFLOW_MARKETING_DESC_1'],
    imageOverride: 'GOOGLE_CLOUD_MONITORING',
    cshid: 'GoogleCloudMonitoring',
    webhookCshid: 'GoogleCloudMonitoringWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/google-cloud-ops/GoogleCloud-1-5.zip',
  },
  {
    id: 'GRAFANA_WORKFLOW',
    title: 'Grafana Alerts',
    categories: ['MONITORING'],
    descriptionIDs: ['GRAFANA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'GRAFANA',
    cshid: 'Grafanav9',
    webhookCshid: 'Grafanav9Webhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/grafana/Grafana-1-4.zip',
  },
  {
    id: 'GRAYLOG_WORKFLOW',
    title: 'Graylog',
    categories: ['MONITORING'],
    descriptionIDs: ['GRAYLOG_WORKFLOW_DESCRIPTION'],
    imageOverride: 'GRAYLOG',
    cshid: 'Graylog',
    webhookCshid: 'GraylogWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/graylog/Graylog-1-3.zip',
  },
  {
    id: 'HALOITSM_WORKFLOW',
    title: 'HaloITSM',
    categories: ['IT_SERVICE_MANAGEMENT'],
    descriptionIDs: ['HALOITSM_WORKFLOW_DESCRIPTION'],
    imageOverride: 'HALOITSM',
    cshid: 'HaloITSM',
    webhookCshid: 'HaloITSMWebhook',
    featureToggle: 'CODELESS_HALOITSM_WORKFLOW',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/haloitsm/HaloITSM-1.zip',
  },
  {
    id: 'HONEYCOMB_WORKFLOW',
    title: 'Honeycomb',
    categories: ['MONITORING'],
    descriptionIDs: ['HONEYCOMB_WORKFLOW_DESCRIPTION'],
    imageOverride: 'HONEYCOMB',
    cshid: 'Honeycomb',
    webhookCshid: 'HoneycombWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/honeycomb/Honeycomb-1-3.zip',
  },
  {
    id: 'HP_OMI',
    title: 'HP BSM OMi',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/hp-bsm-operations-manager-i',
    imageOverride: 'HP',
  },
  {
    id: 'ICINGA_WORKFLOW',
    title: 'Icinga',
    categories: ['MONITORING'],
    descriptionIDs: ['ICINGA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'ICINGA',
    cshid: 'Icinga',
    webhookCshid: 'IcingaWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/icinga/Icinga-1-3.zip',
  },
  {
    id: 'INCIDENT_RESOLUTION',
    title: 'Incident Resolution',
    description: '',
    categories: ['GETTING_STARTED'],
    link: 'http://www.xmatters.com/integration/incident-resolution/',
    cshid: 'IncidentResolutionWorkflow',
    sortOrder: 2,
    featureToggle: 'INCIDENT_MANAGEMENT_MVP',
    packagedWorkflowFeatureToggle: 'INCIDENT_MANAGEMENT_MVP',
    packagedWorkflow: true,
    packageUrl: getIncidentResolutionPackageUrl,
    // COR-48663: Remove this
    // entry from the unfiltered
    // workflow templates
    hideUnlessSearched: true,
  },
  {
    id: 'INSTANA_WORKFLOW',
    title: 'Instana',
    categories: ['MONITORING'],
    descriptionIDs: ['INSTANA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'INSTANA',
    cshid: 'Instana',
    webhookCshid: 'InstanaWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/instana/Instana-1-3.zip',
  },
  {
    id: 'JENKINS',
    title: 'Jenkins',
    categories: ['CONFIGURATION_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/jenkins/',
  },
  {
    id: 'JENKINS_CHANGE_WORKFLOW',
    title: 'Jenkins Changes',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['JENKINS_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'JENKINS',
    cshid: 'Jenkins',
    webhookCshid: 'JenkinsWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/jenkins/JenkinsChanges-1.zip',
  },
  {
    id: 'KASEYA',
    title: 'Kaseya',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/kaseya-vsa-service-desk/',
  },
  {
    id: 'KIALI',
    title: 'Kiali',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/kiali/',
  },
  {
    id: 'LAUNCHDARKLY_CHANGE_WORKFLOW',
    title: 'LaunchDarkly',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['LAUNCHDARKLY_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'LAUNCHDARKLY',
    cshid: 'LaunchDarkly',
    webhookCshid: 'LaunchDarklyWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/launchdarkly/LaunchDarkly-1.zip',
  },
  {
    id: 'LIGHTSTEP_WORKFLOW',
    title: 'Lightstep',
    categories: ['MONITORING'],
    descriptionIDs: ['LIGHTSTEP_WORKFLOW_DESCRIPTION'],
    imageOverride: 'LIGHTSTEP',
    cshid: 'Lightstep',
    webhookCshid: 'LightstepWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/lightstep/Lightstep-1-3.zip',
  },
  {
    id: 'LOGGLY_WORKFLOW',
    title: 'SolarWinds Loggly',
    categories: ['MONITORING'],
    descriptionIDs: ['LOGGLY_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SOLARWINDS',
    cshid: 'Loggly',
    webhookCshid: 'LogglyWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/loggly/Loggly-1-1.zip',
  },
  {
    id: 'LOGIC_MONITOR',
    title: 'LogicMonitor',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/logicmonitor-performance-monitoring-integration/',
  },
  {
    id: 'LOGICMONITOR_WORKFLOW',
    title: 'LogicMonitor',
    categories: ['MONITORING'],
    descriptionIDs: ['LOGICMONITOR_WORKFLOW_DESCRIPTION'],
    imageOverride: 'LOGICMONITOR',
    cshid: 'LogicMonitor',
    webhookCshid: 'LogicMonitorWebhook',
    codelessBuiltInWorkflow: true,
    featureToggle: 'CODELESS_LOGICMONITOR_WORKFLOW',
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/logicmonitor/LogicMonitor-1.zip',
  },
  {
    id: 'LOGZ_IO_WORKFLOW',
    title: 'Logz.io',
    categories: ['MONITORING'],
    descriptionIDs: ['LOGZ_IO_WORKFLOW_DESCRIPTION'],
    imageOverride: 'LOGZIO',
    cshid: 'Logzio',
    webhookCshid: 'LogzioWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/logzio/Logzio-1-4.zip',
  },
  {
    id: 'MANAGE_ENGINE',
    title: 'ManageEngine',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/manageengine/',
  },
  {
    id: 'MICROSOFT_AZURE_BOARDS',
    title: 'Microsoft Azure Boards',
    categories: ['PLANNING_TOOLS'],
    link: 'https://www.xmatters.com/integration/azure-boards/',
  },
  {
    id: 'MICROSOFT_SCOM',
    title: 'Microsoft SCOM',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/microsoft-scom/',
  },
  {
    id: 'MOOGSOFT_WORKFLOW',
    title: 'Moogsoft Alerts',
    categories: ['MONITORING'],
    descriptionIDs: ['MOOGSOFT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'MOOGSOFT',
    cshid: 'Moogsoft',
    webhookCshid: 'MoogsoftWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/moogsoft/Moogsoft-1-4.zip',
  },
  {
    id: 'MOOGSOFT_2WAY_WORKFLOW',
    title: 'Moogsoft Incidents',
    categories: ['MONITORING'],
    descriptionIDs: ['MOOGSOFT_2WAY_WORKFLOW_DESCRIPTION'],
    imageOverride: 'MOOGSOFT',
    cshid: 'MoogsoftIncidents',
    webhookCshid: 'MoogsoftIncidentsWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/moogsoft/MoogsoftIncidents-2.zip',
  },
  {
    id: 'NAGIOS_CORE_WORKFLOW',
    title: 'Nagios Core',
    categories: ['MONITORING'],
    descriptionIDs: ['NAGIOS_CORE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'NAGIOS',
    cshid: 'Nagios',
    webhookCshid: 'NagiosWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/nagios/NagiosCore-1-3.zip',
  },
  {
    id: 'NAGIOS_XI_WORKFLOW',
    title: 'Nagios XI',
    categories: ['MONITORING'],
    descriptionIDs: ['NAGIOS_XI_WORKFLOW_DESCRIPTION'],
    imageOverride: 'NAGIOS',
    cshid: 'Nagios',
    webhookCshid: 'NagiosWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/nagios/NagiosXI-1-3.zip',
  },
  {
    id: 'NEW_RELIC_WORKFLOW_V2',
    title: 'New Relic',
    categories: ['MONITORING'],
    descriptionIDs: ['NEW_RELIC_WORKFLOW_V2_DESCRIPTION'],
    imageOverride: 'NEW_RELIC',
    cshid: 'NewRelic',
    webhookCshid: 'NewRelicWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/new-relic/NewRelic-1-5.zip',
  },
  {
    id: 'ORACLE_CLOUD_CONTROL',
    shortTitle: 'Oracle Cloud Control',
    title: 'Oracle Enterprise Manager Cloud Control',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/oracle-enterprise-manager-cloud-control/',
    imageOverride: 'ORACLE',
  },
  {
    id: 'OVEROPS_WORKFLOW',
    title: 'OverOps',
    categories: ['MONITORING'],
    descriptionIDs: ['OVEROPS_WORKFLOW_DESCRIPTION'],
    imageOverride: 'OVER_OPS',
    cshid: 'OverOps',
    webhookCshid: 'OverOpsWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/overops/OverOps-1-3.zip',
  },
  {
    id: 'PANOPTA_WORKFLOW',
    title: 'Panopta',
    categories: ['MONITORING'],
    descriptionIDs: ['PANOPTA_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PANOPTA',
    cshid: 'Panopta',
    webhookCshid: 'PanoptaWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/panopta/Panopta-1-3.zip',
  },
  {
    id: 'PAPERTRAIL_WORKFLOW',
    title: 'SolarWinds Papertrail',
    categories: ['MONITORING'],
    descriptionIDs: ['PAPERTRAIL_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SOLAR_WINDS_PAPERTRAIL',
    cshid: 'Papertrail',
    webhookCshid: 'PapertrailWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/papertrail/Papertrail-1-2.zip',
  },
  {
    id: 'PGDASH_WORKFLOW',
    title: 'pgDash',
    categories: ['MONITORING'],
    descriptionIDs: ['PGDASH_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PG_DASH',
    cshid: 'pgDash',
    webhookCshid: 'pgDashWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl: 'https://static.xmatters.com/integrations/pgdash/pgDash-2.zip',
  },
  {
    id: 'PINGDOM_SM_WORKFLOW',
    title: 'Pingdom Server Monitor',
    categories: ['MONITORING'],
    descriptionIDs: ['PINGDOM_SM_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PINGDOM',
    cshid: 'Pingdom_SM',
    webhookCshid: 'Pingdom_SMWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/pingdom-sm/PingdomServerMonitor-1-4.zip',
  },
  {
    id: 'PINGDOM_WORKFLOW',
    title: 'Pingdom',
    categories: ['MONITORING'],
    descriptionIDs: ['PINGDOM_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PINGDOM',
    cshid: 'Pingdom',
    webhookCshid: 'PingdomWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/pingdom/Pingdom-1-4.zip',
  },
  {
    id: 'PROMETHEUS_WORKFLOW',
    title: 'Prometheus Alertmanager',
    categories: ['MONITORING'],
    descriptionIDs: ['PROMETHEUS_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PROMETHEUS',
    cshid: 'PrometheusAlertmanager',
    webhookCshid: 'PrometheusWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/prometheus/PrometheusAlertmanager-1-4.zip',
  },
  {
    id: 'PRTG_WORKFLOW',
    title: 'PRTG',
    categories: ['MONITORING'],
    descriptionIDs: ['PRTG_WORKFLOW_DESCRIPTION'],
    imageOverride: 'PRTG',
    cshid: 'PRTG',
    webhookCshid: 'PRTGWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl: 'https://static.xmatters.com/integrations/prtg/PRTG-1-3.zip',
  },
  {
    id: 'SALESFORCE_SERVICE_CLOUD',
    title: 'Salesforce Service Cloud',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/salesforcesc/',
  },
  {
    id: 'SCIENCELOGIC',
    title: 'ScienceLogic',
    categories: ['MONITORING'],
    link: 'https://www.xmatters.com/integration/sciencelogic/',
  },
  {
    id: 'SEMATEXT_WORKFLOW',
    title: 'Sematext',
    categories: ['MONITORING'],
    descriptionIDs: ['SEMATEXT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEMATEXT',
    cshid: 'Sematext',
    webhookCshid: 'SematextWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/sematext/Sematext-1-3.zip',
  },
  {
    id: 'SEND_ALERTS',
    title: 'Send Alerts',
    categories: ['GETTING_STARTED'],
    link: 'http://www.xmatters.com/integration/send-alert-workflow',
    cshid: 'SendAlerts',
    sortOrder: 1,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SendAlerts-1.zip',
  },
  {
    id: 'SENTRY_WORKFLOW',
    title: 'Sentry',
    categories: ['MONITORING'],
    descriptionIDs: ['SENTRY_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SENTRY',
    cshid: 'Sentry',
    webhookCshid: 'SentryWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/sentry/Sentry-1-3.zip',
  },
  {
    id: 'SERVICE_NOW',
    title: 'ServiceNow',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/servicenow',
  },
  {
    id: 'SERVICE_NOW_MIM',
    title: 'ServiceNow MIM',
    categories: ['MAJOR_INCIDENT_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/servicenow-major-incident/',
    imageOverride: 'SERVICE_NOW',
  },
  {
    id: 'SERVICE_NOW_FD_WORKFLOW',
    title: 'ServiceNow (Flow Designer)',
    categories: ['IT_SERVICE_MANAGEMENT'],
    descriptionIDs: ['SERVICE_NOW_FD_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SERVICE_NOW',
    cshid: 'ServiceNowFD',
    featureToggle: 'SERVICENOW_5X_WORKFLOW',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/servicenow/ServiceNowFD-55-2.zip',
  },
  {
    id: 'SIMPLE_EMAIL_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Email',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_EMAIL_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByEmail',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleEmailAlert-1.zip',
    // COR-48663: Remove this
    // entry from the unfiltered
    // workflow templates
    hideUnlessSearched: true,
  },
  {
    id: 'SIMPLE_WEBHOOK_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Webhook',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_WEBHOOK_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByWebhook',
    webhookCshid: 'TriggerAlertsByWebhook',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleWebhookAlert-1.zip',
    // COR-48663: Remove this
    // entry from the unfiltered
    // workflow templates
    hideUnlessSearched: true,
  },
  {
    id: 'SITE24X7_WORKFLOW',
    title: 'Site24x7',
    categories: ['MONITORING'],
    descriptionIDs: ['SITE24X7_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SITE24X7',
    cshid: 'Site24x7',
    webhookCshid: 'Site24x7Webhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/site24x7/Site24x7-1-4.zip',
  },
  {
    id: 'SOLARWINDS_NPM_WORKFLOW',
    title: 'SolarWinds Network Performance Monitor',
    categories: ['MONITORING'],
    descriptionIDs: ['SOLARWINDS_NPM_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SOLARWINDS',
    cshid: 'SolarWindsNPM',
    webhookCshid: 'SolarWindsNPMWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/solarwindsnpm/SolarWindsNPM-1-2.zip',
  },
  {
    id: 'SPINNAKER',
    title: 'Spinnaker',
    categories: ['CONTINUOUS_INTEGRATION'],
    link: 'https://www.xmatters.com/integration/spinnaker/',
  },
  {
    id: 'SPLIT_CHANGE_WORKFLOW',
    title: 'Split',
    categories: ['CHANGE_INTELLIGENCE'],
    descriptionIDs: ['SPLIT_CHANGE_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SPLIT',
    cshid: 'SplitChanges',
    webhookCshid: 'SplitChangesWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl: 'https://static.xmatters.com/integrations/split/Split-1.zip',
  },
  {
    id: 'SPLUNK_IM_WORKFLOW',
    title: 'Splunk Infrastructure Monitoring',
    categories: ['MONITORING'],
    descriptionIDs: ['SPLUNK_IM_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SPLUNK',
    cshid: 'SplunkIM',
    webhookCshid: 'SplunkIMWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/splunk-im/SplunkIM-1-3.zip',
  },
  {
    id: 'SUMO_LOGIC_WORKFLOW',
    title: 'Sumo Logic',
    categories: ['MONITORING'],
    descriptionIDs: ['SUMO_LOGIC_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SUMO_LOGIC',
    cshid: 'SUMO_LOGIC',
    webhookCshid: 'SumoLogicWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/sumologic/SumoLogic-1-3.zip',
  },
  {
    id: 'SYSDIG_WORKFLOW',
    title: 'Sysdig',
    categories: ['MONITORING'],
    descriptionIDs: ['SYSDIG_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SYSDIG',
    cshid: 'Sysdig',
    webhookCshid: 'SysdigWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/sysdig/Sysdig-1-2.zip',
  },
  {
    id: 'TERRA_FORM',
    title: 'TerraForm',
    categories: ['CONFIGURATION_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/terraform/',
  },
  {
    id: 'THOUSANDEYES_WORKFLOW',
    title: 'ThousandEyes',
    categories: ['MONITORING'],
    descriptionIDs: ['THOUSANDEYES_WORKFLOW_DESCRIPTION'],
    imageOverride: 'THOUSANDEYES',
    cshid: 'ThousandEyes',
    webhookCshid: 'ThousandEyesWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/thousandeyes/ThousandEyes-1-3.zip',
  },
  {
    id: 'ZABBIX_WORKFLOW',
    title: 'Zabbix',
    categories: ['MONITORING'],
    descriptionIDs: ['ZABBIX_WORKFLOW_DESCRIPTION'],
    imageOverride: 'ZABBIX',
    cshid: 'Zabbix',
    webhookCshid: 'ZabbixWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/zabbix/Zabbix-1-4.zip',
  },
  {
    id: 'ZENDESK_V2_WORKFLOW',
    title: 'Zendesk',
    categories: ['IT_SERVICE_MANAGEMENT'],
    descriptionIDs: ['ZENDESK_V2_WORKFLOW_DESCRIPTION'],
    imageOverride: 'ZENDESK',
    packagedWorkflow: true,
    codelessBuiltInWorkflow: true,
    link: 'https://help.xmatters.com/integrations/itmgmt/zendesk-workflow.htm',
    packageUrl:
      'https://static.xmatters.com/integrations/zendesk/ZendeskAlerts-1-2.zip',
  },
];

// These are replaced integrations that are no longer available to be created but editable.
// When moving to this section from other sections, please add `replacedTo` field with id
// of the new version of the replaced integration.
export const REPLACED = {
  AWS_CLOUDWATCH_WORKFLOW: {
    id: 'AWS_CLOUDWATCH_WORKFLOW',
    title: 'AWS CloudWatch',
    categories: ['MONITORING'],
    descriptionIDs: ['AWS_CLOUDWATCH_WORKFLOW_DESCRIPTION'],
    imageOverride: 'AWS_CLOUDWATCH',
    cshid: 'AWSCloudWatch',
    webhookCshid: 'AWSCloudWatchWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/aws-cloudwatch/AWSCloudWatch-1-7.zip',
    replacedTo: 'AWS_CLOUDWATCH_V2_WORKFLOW',
  },
  APP_DYNAMICS: {
    id: 'APP_DYNAMICS',
    title: 'AppDynamics',
    categories: ['MONITORING'],
    targetFormNames: ['AppDynamics Event'],
    descriptionIDs: [
      'APP_DYNAMICS_MARKETING_DESC_1',
      'APP_DYNAMICS_MARKETING_DESC_2',
    ],
    replacedTo: 'APPDYNAMICS_WORKFLOW',
  },
  BIGPANDA: {
    id: 'BIGPANDA',
    title: 'BigPanda',
    categories: ['MONITORING'],
    targetFormNames: ['AutoShare webhook'],
    descriptionIDs: ['BIGPANDA_MARKETING_DESC_1', 'BIGPANDA_MARKETING_DESC_2'],
    replacedTo: 'BIGPANDA_WORKFLOW',
  },
  DATADOG: {
    id: 'DATADOG',
    title: 'Datadog',
    categories: ['MONITORING'],
    targetFormNames: [
      'Conference Bridge',
      'Datadog Alert',
      'Engage with xMatters',
      "Who's on Call",
    ],
    descriptionIDs: ['DATADOG_MARKETING_DESC_1', 'DATADOG_MARKETING_DESC_2'],
    replacedTo: 'DATADOG_WORKFLOW',
  },
  DYNATRACE: {
    id: 'DYNATRACE',
    title: 'Dynatrace',
    categories: ['MONITORING'],
    targetFormNames: ['Dynatrace Alerts'],
    descriptionIDs: [
      'DYNATRACE_MARKETING_DESC_1',
      'DYNATRACE_MARKETING_DESC_2',
    ],
    replacedTo: 'DYNATRACE_WORKFLOW',
  },
  ELASTIC: {
    id: 'ELASTIC_WORKFLOW',
    title: 'Elastic',
    categories: ['MONITORING'],
    descriptionIDs: ['ELASTIC_WORKFLOW_DESCRIPTION'],
    imageOverride: 'ELASTIC',
    cshid: 'Elastic',
    webhookCshid: 'ElasticWebhook',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/elastic/Elastic-1-2.zip',
    replacedTo: 'ELASTIC_CONNECTOR_WORKFLOW',
  },
  ICINGA: {
    id: 'ICINGA',
    title: 'Icinga',
    categories: ['MONITORING'],
    targetFormNames: ['Icinga2 Notification'],
    descriptionIDs: ['ICINGA_MARKETING_DESC_1', 'ICINGA_MARKETING_DESC_2'],
    replacedTo: 'ICINGA_WORKFLOW',
  },
  NEW_RELIC: {
    id: 'NEW_RELIC',
    title: 'New Relic',
    categories: ['MONITORING'],
    targetFormNames: ['New Relic Event'],
    descriptionIDs: [
      'NEW_RELIC_MARKETING_DESC_1',
      'NEW_RELIC_MARKETING_DESC_2',
    ],
    replacedTo: 'NEW_RELIC_WORKFLOW_V2',
  },
  PINGDOM: {
    id: 'PINGDOM',
    title: 'Pingdom',
    categories: ['MONITORING'],
    targetFormNames: ['New Alert'],
    descriptionIDs: ['PINGDOM_MARKETING_DESC_1', 'PINGDOM_MARKETING_DESC_2'],
    replacedTo: 'PINGDOM_WORKFLOW',
  },
  PINGDOM_SM: {
    id: 'PINGDOM_SM',
    title: 'Pingdom Server Monitor',
    categories: ['MONITORING'],
    targetFormNames: ['Alert'],
    imageOverride: 'PINGDOM',
    descriptionIDs: [
      'PINGDOM_SM_MARKETING_DESC_1',
      'PINGDOM_SM_MARKETING_DESC_2',
    ],
    replacedTo: 'PINGDOM_SM_WORKFLOW',
  },
  SIGNALFX: {
    id: 'SIGNALFX',
    title: 'SignalFx',
    categories: ['MONITORING'],
    targetFormNames: ['SignalFx Alerts'],
    descriptionIDs: ['SIGNALFX_MARKETING_DESC_1', 'SIGNALFX_MARKETING_DESC_2'],
    replacedTo: 'SPLUNK_IM_WORKFLOW',
  },
  SOLAR_WINDS: {
    id: 'SOLAR_WINDS',
    title: 'SolarWinds Network Performance Monitor',
    shortTitle: 'SolarWinds NPM',
    categories: ['MONITORING'],
    targetFormNames: ['Alert'],
    descriptionIDs: [
      'SOLAR_WINDS_MARKETING_DESC_1',
      'SOLAR_WINDS_MARKETING_DESC_2',
    ],
    replacedTo: 'SOLARWINDS_NPM_WORKFLOW',
  },
  STACKDRIVER: {
    id: 'STACKDRIVER',
    title: 'Google Cloud Operations Suite',
    categories: ['MONITORING'],
    targetFormNames: ['Google Cloud Monitoring'],
    descriptionIDs: ['STACKDRIVER_MARKETING_DESC_1'],
    imageOverride: 'GOOGLE_CLOUD_MONITORING',
    additionalSearchableText: ['Stackdriver'],
    replacedTo: 'GOOGLE_CLOUD_OPS_WORKFLOW',
  },
  SUMO_LOGIC: {
    id: 'SUMO_LOGIC',
    title: 'Sumo Logic',
    categories: ['LOG_MANAGEMENT'],
    targetFormNames: ['Sumo Logic Event'],
    descriptionIDs: [
      'SUMO_LOGIC_MARKETING_DESC_1',
      'SUMO_LOGIC_MARKETING_DESC_2',
    ],
    replacedTo: 'SUMO_LOGIC_WORKFLOW',
  },
  ZENDESK: {
    id: 'ZENDESK',
    title: 'Zendesk',
    categories: ['IT_SERVICE_MANAGEMENT'],
    link: 'https://www.xmatters.com/integration/zendesk-workflow/',
    replacedTo: 'ZENDESK_V2_WORKFLOW',
  },
};

// //
// Begin `WORKFLOW_TEMPLATES_ORGANIZATION`-specific data
// The following consists of data that will be used specifically
// the above feature toggle is enabled
// //
export const BASIC_WORKFLOWS = [
  {
    id: 'SIMPLE_EMAIL_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Email',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_EMAIL_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByEmail',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleEmailAlert-1.zip',
  },
  {
    id: 'SIMPLE_WEBHOOK_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Webhook',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_WEBHOOK_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByWebhook',
    webhookCshid: 'TriggerAlertsByWebhook',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleWebhookAlert-1.zip',
  },
  {
    id: 'INCIDENT_RESOLUTION',
    title: 'Incident Resolution',
    description: '',
    categories: ['GETTING_STARTED'],
    link: 'http://www.xmatters.com/integration/incident-resolution/',
    cshid: 'IncidentResolutionWorkflow',
    sortOrder: 2,
    featureToggle: 'INCIDENT_MANAGEMENT_MVP',
    hideIfFeatureToggleOff: true, // https://trail.everbridge.com/browse/COR-68677
    packagedWorkflowFeatureToggle: 'INCIDENT_MANAGEMENT_MVP',
    packagedWorkflow: true,
    packageUrl: getIncidentResolutionPackageUrl,
  },
  {
    id: 'CUSTOM_WORKFLOW',
    titleId: 'CUSTOM_WORKFLOW',
    categories: ['ANYTHING_AND_EVERYTHING'],
    sortOrder: 0,
    cshid: 'CreateWorkflow',
  },
];

export const EMPTY_STATE_CTA_WORKFLOWS = [
  {
    id: 'SIMPLE_EMAIL_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Email',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_EMAIL_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByEmail',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleEmailAlert-1.zip',
  },
  {
    id: 'SIMPLE_WEBHOOK_ALERT_WORKFLOW',
    title: 'Trigger Alerts by Webhook',
    categories: ['GETTING_STARTED'],
    descriptionIDs: ['SIMPLE_WEBHOOK_ALERT_WORKFLOW_DESCRIPTION'],
    imageOverride: 'SEND_ALERTS',
    cshid: 'TriggerAlertsByWebhook',
    webhookCshid: 'TriggerAlertsByWebhook',
    sortOrder: 1,
    featureToggle: 'CODELESS_SIMPLE_WEBHOOK_WORKFLOW',
    codelessBuiltInWorkflow: true,
    packagedWorkflow: true,
    packageUrl:
      'https://static.xmatters.com/integrations/xmatters/SimpleWebhookAlert-1.zip',
  },
  {
    id: 'CUSTOM_WORKFLOW',
    titleId: 'CUSTOM_WORKFLOW',
    categories: ['ANYTHING_AND_EVERYTHING'],
    sortOrder: 0,
    cshid: 'CreateWorkflow',
  },
];
// //
// End `WORKFLOW_TEMPLATES_ORGANIZATION`-specific data
// //

export const NON_PACKAGED_MAP = NON_PACKAGED.reduce(
  (acc, val) => ({
    ...acc,
    [val.id]: val,
  }),
  {},
);

export const PACKAGED_WORKFLOW = keyBy(
  NON_PACKAGED.filter(v => v.packagedWorkflow),
  'id',
);

export const NON_PACKAGED_WORKFLOW = keyBy(
  NON_PACKAGED.filter(v => v.link),
  'id',
);

export const COMBINED = {
  ...PACKAGED,
  ...NON_PACKAGED_MAP,
  ...DEPRECATED,
  ...NOT_INTEGRATIONS,
  ...REPLACED,
  [CUSTOM_WORKFLOW.id]: CUSTOM_WORKFLOW,
};

export const getImageForType = type => {
  if (!type) return CUSTOM_WORKFLOW.id;

  let d = COMBINED[type];

  if (!d) return CUSTOM_WORKFLOW.id;

  if (d.typeOverride) {
    d = COMBINED[d.typeOverride];
  }

  if (d.imageOverride) {
    return d.imageOverride;
  }

  return d.id;
};

export const getImageExtensionForType = type => {
  let extension = 'png';

  if (type) {
    const d = COMBINED[type];
    if (d && d.codelessBuiltInWorkflow) {
      extension = 'svg';
    }
  }
  return extension;
};

export const getTitleForType = type => COMBINED[type]?.title;
export const getWebhookCshidForType = type => COMBINED[type].webhookCshid;

export const getCategoriesForType = type => {
  const desc = COMBINED[type];
  return desc.categories || [];
};

const WORKFLOW_UI_DESCRIPTIONS = {
  SCIENCELOGIC_DESCRIPTION: 'SCIENCELOGIC_DESCRIPTION_WORKFLOW',
};

function getWorkflowDescription(descriptionId) {
  if (descriptionId in WORKFLOW_UI_DESCRIPTIONS) {
    return WORKFLOW_UI_DESCRIPTIONS[descriptionId];
  }
  return descriptionId;
}

export const getShortDescriptionIdForType = type =>
  getWorkflowDescription(`${type}_DESCRIPTION`);

export const getLongDescriptionIdsForType = type => {
  const desc = COMBINED[type];
  if (!desc.descriptionIDs) {
    return [];
  }
  return desc.descriptionIDs.map(descriptionId =>
    getWorkflowDescription(descriptionId),
  );
};

export const getAdditionalInfoIdsForType = type => {
  const workflow = COMBINED[type];
  return workflow.additionalInfoIDs?.map(infoId => infoId) || [];
};

export const getCustomHelpMessageIdForType = type => {
  const workflow = COMBINED[type];
  return workflow.customHelpMessageId || '';
};

export const getCustomHelpLinkIdForType = type => {
  const workflow = COMBINED[type];
  return workflow.customHelpLinkId || '';
};
